import { getSignedDownloadUrl } from './storageService';

const DB_NAME = 'replico-offline';
const DB_VERSION = 1;
const STORE_ASSETS = 'assets';
const STORE_PATIENT_STATUS = 'patientStatus';
const STORE_PATIENTS = 'patients';
const SERVICE_WORKER_PATH = `${import.meta.env.PUBLIC_URL || ''}/service-worker.js`;
const SERVICE_WORKER_SCOPE = import.meta.env.PUBLIC_URL ? `${import.meta.env.PUBLIC_URL}/` : '/';

export const OFFLINE_FILE_KEYS = {
  MRI_VOLUME: 'mri_volume',
  SEGMENTATION: 'segmentation',
  MODEL: 'model'
};

export const OFFLINE_STATUS = {
  IDLE: 'idle',
  DOWNLOADING: 'downloading',
  READY: 'ready',
};

/** @type {Map<string, Set<Function>>} */
const statusListeners = new Map();

/** @type {Set<string>} */
const activeOfflineDownloads = new Set();

/** @type {Map<string, AbortController[]>} */
const activeOfflineDownloadControllers = new Map();

/** @type {Promise<IDBDatabase> | null} */
let dbPromise = null;

function openOfflineDb() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_ASSETS)) {
        const assetsStore = db.createObjectStore(STORE_ASSETS, { keyPath: 'id' });
        assetsStore.createIndex('patientId', 'patientId', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_PATIENT_STATUS)) {
        db.createObjectStore(STORE_PATIENT_STATUS, { keyPath: 'patientId' });
      }
      if (!db.objectStoreNames.contains(STORE_PATIENTS)) {
        db.createObjectStore(STORE_PATIENTS, { keyPath: 'patientId' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };
  });

  return dbPromise;
}

/**
 * @param {string} patientId
 * @param {string} status
 */
/**
 * @template T
 * @param {IDBRequest<T>} request
 * @returns {Promise<T>}
 */
function requestPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * @param {string} patientId
 * @param {string} status
 */
function notifyStatusListeners(patientId, status) {
  const listeners = statusListeners.get(patientId);
  if (listeners) listeners.forEach((/** @type {Function} */ cb) => cb(status));
}

/**
 * @param {string} patientId
 * @param {string} status
 * @returns {Promise<void>}
 */
export async function writePatientStatus(patientId, status, downloadPercent = 0) {
  const db = await openOfflineDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PATIENT_STATUS, 'readwrite');
    const record = { patientId, status, downloadPercent, updatedAt: Date.now() };
    const request = tx.objectStore(STORE_PATIENT_STATUS).put(record);
    requestPromise(request).then(() => {
      notifyStatusListeners(patientId, status);
      resolve(undefined);
    }).catch(reject);
  });
}

/**
 * Returns the current offline status record for a patient.
 * @param {string} patientId
 * @returns {Promise<{patientId: string, status: 'idle'|'downloading'|'ready', downloadPercent: number}>}
 */
export async function getPatientOfflineStatusRecord(patientId) {
  const db = await openOfflineDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PATIENT_STATUS, 'readonly');
    const request = tx.objectStore(STORE_PATIENT_STATUS).get(patientId);
    requestPromise(request).then((result) => {
      if (!result) {
        resolve({ patientId, status: /** @type {'idle'|'downloading'|'ready'} */ (OFFLINE_STATUS.IDLE), downloadPercent: 0 });
        return;
      }
      resolve({
        patientId,
        status: /** @type {'idle'|'downloading'|'ready'} */ (result.status ?? OFFLINE_STATUS.IDLE),
        downloadPercent: typeof result.downloadPercent === 'number' ? result.downloadPercent : 0,
      });
    }).catch(reject);
  });
}

/**
 * @param {string} patientId
 * @param {Array<{fileKey: string, blob: Blob}>} records
 * @returns {Promise<void>}
 */
async function storeAssetBlobs(patientId, records) {
  const db = await openOfflineDb();
  return /** @type {Promise<void>} */ (new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_ASSETS, 'readwrite');
    const store = tx.objectStore(STORE_ASSETS);

    for (const { fileKey, blob } of records) {
      store.put({
        id: `${patientId}:${fileKey}`,
        patientId,
        fileKey,
        blob,
        size: blob.size,
        storedAt: Date.now(),
      });
    }

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}

/**
 * @param {string} patientId
 * @param {string} displayName
 * @param {number} createdTime
 * @returns {Promise<void>}
 */
async function storePatientRecord(patientId, displayName, createdTime) {
  const db = await openOfflineDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PATIENTS, 'readwrite');
    const record = { patientId, displayName, createdTime, storedAt: Date.now() };
    const request = tx.objectStore(STORE_PATIENTS).put(record);
    requestPromise(request).then(() => resolve(undefined)).catch(reject);
  });
}

/**
 * Returns the current offline status for a patient.
 * @param {string} patientId
 * @returns {Promise<'idle'|'downloading'|'ready'>}
 */
export async function getPatientOfflineStatus(patientId) {
  const db = await openOfflineDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PATIENT_STATUS, 'readonly');
    const request = tx.objectStore(STORE_PATIENT_STATUS).get(patientId);
    requestPromise(request).then((result) => resolve(result?.status ?? OFFLINE_STATUS.IDLE)).catch(reject);
  });
}

/**
 * Returns the stored patient record from IndexedDB, or null if not found.
 * @param {string} patientId
 * @returns {Promise<{patientId: string, displayName: string, createdTime: number} | null>}
 */
export async function getOfflinePatientRecord(patientId) {
  const db = await openOfflineDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PATIENTS, 'readonly');
    const request = tx.objectStore(STORE_PATIENTS).get(patientId);
    requestPromise(request).then((result) => resolve(result ?? null)).catch(reject);
  });
}

/**
 * Resolves demo asset URLs from Firebase Storage for the offline download flow.
 * This helper belongs in the service layer because it maps application keys to storage paths.
 *
 * @param {string} patientId
 * @returns {Promise<Record<string, string>>}
 */
export async function resolveDemoAssetUrls(patientId) {
  const [mriUrl, segUrl, modelUrl] = await Promise.all([
    getSignedDownloadUrl({ asset: 'mri_volume', patientId }),
    getSignedDownloadUrl({ asset: 'segmentation', patientId }),
    getSignedDownloadUrl({ asset: 'model', patientId }),
  ]);

  return {
    [OFFLINE_FILE_KEYS.MRI_VOLUME]: mriUrl,
    [OFFLINE_FILE_KEYS.SEGMENTATION]: segUrl,
    [OFFLINE_FILE_KEYS.MODEL]: modelUrl,
  };
}

/**
 * Fetches a URL and streams the response, calling onBytes with (received, total) as bytes arrive.
 * Falls back to response.blob() if ReadableStream is unavailable.
 *
 * @param {string} url
 * @param {AbortSignal} [signal]
 * @param {(received: number, total: number) => void} [onBytes]
 * @returns {Promise<Blob>}
 */
async function fetchWithProgress(url, signal, onBytes) {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const contentLength = response.headers.get('content-length');
  const total = contentLength ? parseInt(contentLength, 10) : 0;

  if (!total || !response.body) {
    const blob = await response.blob();
    onBytes?.(blob.size, blob.size);
    return blob;
  }

  const reader = response.body.getReader();
  const chunks = /** @type {BlobPart[]} */ ([]);
  let received = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.byteLength;
    onBytes?.(received, total);
  }

  return new Blob(chunks);
}

/**
 * Downloads all assets for a patient from the provided URL map and stores them in IndexedDB.
 * No-ops if the patient is already ready.
 *
 * @param {string} patientId
 * @param {Record<string, string>} urlMap - map of OFFLINE_FILE_KEYS value to Firebase Storage download URL
 * @param {(progress: { completed: number, total: number, fileKey: string, percent: number }) => void} [onProgress]
 * @param {{ displayName: string, createdTime: number }} [patientRecord] - patient metadata to persist alongside assets
 * @returns {Promise<void>}
 */
export async function downloadPatientAssets(patientId, urlMap, onProgress, patientRecord) {
  const currentStatus = await getPatientOfflineStatus(patientId);
  if (currentStatus === OFFLINE_STATUS.READY) {
    return;
  }

  activeOfflineDownloads.add(patientId);

  // If not already marked as DOWNLOADING (e.g. hook wrote it before calling us), write it now
  if (currentStatus !== OFFLINE_STATUS.DOWNLOADING) {
    await writePatientStatus(patientId, OFFLINE_STATUS.DOWNLOADING, 0);
  }

  const fileKeys = Object.keys(urlMap);
  if (fileKeys.length === 0) {
    await writePatientStatus(patientId, OFFLINE_STATUS.READY);
    return;
  }
  const n = fileKeys.length;
  const fileProgress = new Array(n).fill(0);

  const emitProgress = (/** @type {string} */ fileKey) => {
    const completedFiles = fileProgress.filter((progress) => progress === 1).length;
    const overall = fileProgress.reduce((a, b) => a + b, 0) / n;
    onProgress?.({
      completed: completedFiles,
      total: n,
      fileKey,
      percent: Math.min(Math.round(overall * 100), 99),
    });
  };

  try {
    const controllers = fileKeys.map(() => new AbortController());
    activeOfflineDownloadControllers.set(patientId, controllers);
    let lastPersistedPercent = 0;
    const downloadPromises = fileKeys.map((fileKey, index) => {
      fileProgress[index] = 0.05;
      emitProgress(fileKey);

      return fetchWithProgress(urlMap[fileKey], controllers[index].signal, (received, total) => {
        fileProgress[index] = total > 0 ? received / total : fileProgress[index];
        emitProgress(fileKey);
        const percent = Math.min(Math.round((fileProgress.reduce((a, b) => a + b, 0) / n) * 100), 99);
        if (percent !== lastPersistedPercent) {
          lastPersistedPercent = percent;
          writePatientStatus(patientId, OFFLINE_STATUS.DOWNLOADING, percent).catch(() => {});
        }
      }).then((blob) => {
        fileProgress[index] = 1;
        emitProgress(fileKey);
        return { fileKey, blob };
      });
    });
    let assetBlobs;
    try {
      assetBlobs = await Promise.all(downloadPromises);
    } catch (err) {
      controllers.forEach((controller) => controller.abort());
      throw err;
    }
    await storeAssetBlobs(patientId, assetBlobs);

    if (patientRecord) {
      try {
        await storePatientRecord(patientId, patientRecord.displayName, patientRecord.createdTime);
      } catch (err) {
        console.warn('Failed to store offline patient metadata', err);
      }
    }

    await writePatientStatus(patientId, OFFLINE_STATUS.READY, 100);
  } catch (err) {
    if (/** @type {any} */ (err)?.name !== 'AbortError') {
      try {
        await deletePatientOfflineData(patientId);
      } catch (cleanupErr) {
        console.warn('Failed to cleanup partial offline download for patient:', patientId, cleanupErr);
      }
    }
    throw err;
  } finally {
    activeOfflineDownloads.delete(patientId);
    activeOfflineDownloadControllers.delete(patientId);
  }
}

/**
 * Returns whether an offline download is still active in the current app runtime.
 * This stays true across component navigation, but resets after a hard refresh.
 *
 * @param {string} patientId
 * @returns {boolean}
 */
export function isOfflineDownloadActive(patientId) {
  return activeOfflineDownloads.has(patientId);
}

/**
 * Aborts all active offline downloads and clears their partial persisted state.
 * Used during logout so session-scoped downloads do not continue in the background.
 *
 * @returns {Promise<void>}
 */
export async function abortActiveOfflineDownloads() {
  const patientIds = Array.from(activeOfflineDownloads);

  patientIds.forEach((patientId) => {
    const controllers = activeOfflineDownloadControllers.get(patientId) || [];
    controllers.forEach((controller) => controller.abort());
  });

  await Promise.all(patientIds.map(async (patientId) => {
    try {
      await deletePatientOfflineData(patientId);
    } catch (err) {
      console.warn('Failed to cleanup aborted offline download for patient:', patientId, err);
    }
  }));
}

/**
 * Retrieves a blob URL for a stored asset.
 * The caller is responsible for calling URL.revokeObjectURL() when done.
 *
 * @param {string} patientId
 * @param {string} fileKey - one of OFFLINE_FILE_KEYS values
 * @returns {Promise<string|null>} object URL or null if not cached
 */
export async function getPatientBlobUrl(patientId, fileKey) {
  const db = await openOfflineDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_ASSETS, 'readonly');
    const request = tx.objectStore(STORE_ASSETS).get(`${patientId}:${fileKey}`);
    requestPromise(request).then((result) => {
      if (!result) {
        resolve(null);
        return;
      }
      resolve(URL.createObjectURL(result.blob));
    }).catch(reject);
  });
}

/**
 * Subscribes to offline status changes for a patient.
 * Returns an unsubscribe function.
 *
 * @param {string} patientId
 * @param {(status: 'idle'|'downloading'|'ready') => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeToPatientOfflineStatus(patientId, callback) {
  if (!statusListeners.has(patientId)) {
    statusListeners.set(patientId, new Set());
  }
  /** @type {Set<Function>} */ (statusListeners.get(patientId)).add(callback);

  return () => {
    const listeners = statusListeners.get(patientId);
    if (!listeners) return;
    listeners.delete(callback);
    if (listeners.size === 0) statusListeners.delete(patientId);
  };
}

/**
 * Clears all offline assets and status records from IndexedDB.
 * Called when a non-demo user logs in to prevent cross-role data exposure.
 *
 * @returns {Promise<void>}
 */
export async function clearAllOfflineData() {
  const db = await openOfflineDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_ASSETS, STORE_PATIENT_STATUS, STORE_PATIENTS], 'readwrite');
    tx.objectStore(STORE_ASSETS).clear();
    tx.objectStore(STORE_PATIENT_STATUS).clear();
    tx.objectStore(STORE_PATIENTS).clear();
    tx.oncomplete = () => {
      statusListeners.clear();
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Returns all patients that have been fully downloaded (status = READY),
 * sorted by createdTime descending then displayName ascending.
 * Used to populate the patient grid when the device is offline.
 *
 * @returns {Promise<Array<{ patientId: string, displayName: string, createdTime: number }>>}
 */
export async function getOfflinePatients() {
  const db = await openOfflineDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_PATIENTS, STORE_PATIENT_STATUS], 'readonly');
    let patients = /** @type {any[] | null} */ (null);
    let statuses = /** @type {any[] | null} */ (null);
    let failed = false;

    const tryResolve = () => {
      if (failed || patients === null || statuses === null) return;
      const readyIds = new Set(
        statuses.filter(s => s.status === OFFLINE_STATUS.READY).map(s => s.patientId)
      );
      const result = patients
        .filter(p => readyIds.has(p.patientId))
        .sort((a, b) => {
          if (a.createdTime !== b.createdTime) return b.createdTime - a.createdTime;
          return a.displayName.localeCompare(b.displayName);
        });
      resolve(result);
    };

    const patientsReq = tx.objectStore(STORE_PATIENTS).getAll();
    const statusReq = tx.objectStore(STORE_PATIENT_STATUS).getAll();

    patientsReq.onsuccess = () => { patients = patientsReq.result; tryResolve(); };
    statusReq.onsuccess = () => { statuses = statusReq.result; tryResolve(); };
    tx.onerror = () => { failed = true; reject(tx.error); };
  });
}

/**
 * Deletes all offline data (assets, status, patient record) for a single patient.
 * Notifies subscribers so the UI transitions back to IDLE immediately.
 *
 * @param {string} patientId
 * @returns {Promise<void>}
 */
export async function deletePatientOfflineData(patientId) {
  const db = await openOfflineDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_ASSETS, STORE_PATIENT_STATUS, STORE_PATIENTS], 'readwrite');

    // Delete all asset blobs for this patient via the patientId index
    const assetsStore = tx.objectStore(STORE_ASSETS);
    const index = assetsStore.index('patientId');
    const cursorReq = index.openKeyCursor(IDBKeyRange.only(patientId));
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor) {
        assetsStore.delete(cursor.primaryKey);
        cursor.continue();
      }
    };

    tx.objectStore(STORE_PATIENT_STATUS).delete(patientId);
    tx.objectStore(STORE_PATIENTS).delete(patientId);

    tx.oncomplete = () => {
      notifyStatusListeners(patientId, OFFLINE_STATUS.IDLE);
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Keeps service worker registration aligned with whether the current user is a demo user.
 *
 * @param {boolean} isDemo
 * @returns {Promise<void>}
 */
export async function syncOfflineServiceWorker(isDemo) {
  if (!(typeof navigator !== 'undefined' && 'serviceWorker' in navigator)) return;

  if (isDemo) {
    try {
      await navigator.serviceWorker.register(SERVICE_WORKER_PATH, { scope: SERVICE_WORKER_SCOPE });
    } catch (err) {
      console.warn('Service worker registration failed:', err);
    }
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch (err) {
    console.warn('Service worker unregistration failed:', err);
  }
}

/**
 * Clears demo offline data and ensures no offline service worker remains active.
 *
 * @returns {Promise<void>}
 */
export async function resetOfflineDemoState() {
  await clearAllOfflineData();
  await syncOfflineServiceWorker(false);
}
